// v49.42 — lcAudit helper.
// Centralised, fire-and-forget audit log writer. Every mutating action
// across the platform calls this so the /audit page has a complete
// trail without each callsite having to remember the actor lookup +
// metadata shape.
//
// Schema reference (migration-001):
//   audit_events (
//     id, event_type, actor_id, actor_email, actor_company, deal_id,
//     occurred_at, ip_address, user_agent, ui_string, metadata
//   )
//
// Conventions:
//   • event_type uses dot.snake_case: invoice.created, milestone.evidence_submitted
//   • ui_string captures the human-friendly action label (eg "Created INV-…-001")
//   • metadata carries IDs + amounts for downstream querying

import { supabase } from "./api/supabase-client";

export interface AuditCall {
  event_type: string;
  ui_string?: string | null;
  metadata?:  Record<string, unknown>;
}

let _profileCache: { email?: string | null; company?: string | null; userId?: string | null } | null = null;

async function resolveActor() {
  if (_profileCache) return _profileCache;
  try {
    const { data } = await supabase.auth.getSession();
    const u = data?.session?.user;
    if (!u) { _profileCache = { email: null, company: null, userId: null }; return _profileCache; }
    /* Lookup once per session — pull display values from profiles row. */
    const { data: profile } = await supabase
      .from("profiles")
      .select("email, company")
      .eq("id", u.id)
      .maybeSingle();
    _profileCache = {
      email:   profile?.email   || u.email || null,
      company: profile?.company || null,
      userId:  u.id,
    };
    return _profileCache;
  } catch {
    _profileCache = { email: null, company: null, userId: null };
    return _profileCache;
  }
}

/* Hook the auth onAuthStateChange invalidator so a sign-out → new
   sign-in flow doesn't keep the previous user's cached actor. Bound
   lazily here to avoid an import cycle through auth.ts. */
if (typeof window !== "undefined") {
  try {
    supabase.auth.onAuthStateChange(() => { _profileCache = null; });
  } catch { /* ignore */ }
}

export function lcAudit(call: AuditCall): void {
  /* Fire-and-forget — never block the caller. Errors are warned, not
     thrown, so an audit failure doesn't break the main action. */
  void (async () => {
    try {
      const actor = await resolveActor();
      const userAgent = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null;
      const row = {
        event_type:    call.event_type,
        actor_id:      actor.userId,
        actor_email:   actor.email,
        actor_company: actor.company,
        user_agent:    userAgent,
        ui_string:     call.ui_string || null,
        metadata:      call.metadata || {},
      };
      const { error } = await supabase.from("audit_events").insert(row);
      if (error) console.warn("[lcAudit] insert failed:", call.event_type, error);
    } catch (e) {
      console.warn("[lcAudit] threw:", call.event_type, e);
    }
  })();
}
